import { DefineComponentProps } from 'utils'
import { useBaseInfoStore, useExchangeInfoStore, useProjectTheme, useUserCenterContractOrderStore } from 'store'
import { useI18n } from 'i18n-utils'
import { HocElDialog } from '../hoc-element-components/HocElDialog'
import { Tag } from '../components/Tag'
import { ListCell } from '../components/ListCell'
import { DialogHeader } from '../components/DialogHeader'
import { useDialog } from '../stores/use-dialog'
import { OrderDirection } from 'network'
import { dayjs } from 'element-plus'
import { Tabs, TabPane } from '../components/Tabs'
import { PositionItemLayout } from '../layouts/PositionInfo.layout'
import { IcebergTradeHistory } from './IcebergTradeHistory'
import { ContractTypeIces } from 'store/src/constants/common'
import './StrategyDetail.scss'

enum StrategyDetailTab {
  StrategyInfo = 'strategyInfo',
  TradeRecord = 'tradeRecord',
}

export type StrategyDetailProps = {
  strategy: ApiResponse.HistoricalStrategy
  onClose?: () => void
}

export const StrategyDetail = defineComponent<StrategyDetailProps>({
  props: DefineComponentProps<StrategyDetailProps>(['strategy', 'onClose']),
  setup(props) {
    const { openDialog, closeDialog, dialogVisible } = useDialog()
    const baseInfoStore = useBaseInfoStore()
    const exchangeStore = useExchangeInfoStore()
    const themeStore = useProjectTheme()
    const theme = computed(() => themeStore.theme)
    const contractOrderListStore = useUserCenterContractOrderStore()
    const { t } = useI18n()

    const activeTab = ref(StrategyDetailTab.StrategyInfo)

    const coinIcon = computed(() => {
      return baseInfoStore.getContractCoinIconUrlBySymbolId(props.strategy.symbolId) || ''
    })

    const strategyStatus = computed(() => {
      return contractOrderListStore.getHistoricalStrategyStatus(props.strategy)
    })

    // 计算运行时长
    const runtime = computed(() => {
      const startTime = props.strategy.createTime
      const endTime = props.strategy.updateTime
      const duration = endTime.subtractStr(startTime).divideStr(1000)
      const days = duration.divideStr(24 * 3600).cutDecimal(0)
      const time2 = duration.modeStr(24 * 3600)
      const hour = time2.divideStr(3600).cutDecimal(0)
      const time3 = duration.modeStr(3600)
      const minus = time3.divideStr(60).ceil(0).cutDecimal(0)
      return `${days}日${hour}时${minus}分`
    })

    // 成交均价
    const avgPrice = computed(() => {
      if (props.strategy.avgPrice && Number(props.strategy.avgPrice) > 0) {
        return props.strategy.avgPrice.formatCommonMoney()
      }
      return '--'
    })

    onBeforeMount(() => {
      openDialog()
    })

    const onClose = () => {
      if (!dialogVisible.value) return
      closeDialog()
      props.onClose?.()
    }

    return () => {
      const data = props.strategy

      return (
        <HocElDialog v-model={dialogVisible.value} showClose={false} width={700} onClose={onClose} placement="right" offsetTop={0} class="header-pb-0">
          {{
            header: () => <DialogHeader onClose={onClose}>{t('策略详情')}</DialogHeader>,
            default: () => (
              <div class="strategy-detail-layout">
                <PositionItemLayout style={{ borderBottom: 'none' }}>
                  {/* 顶部币种信息 */}
                  <div class="symbol-coin-info">
                    <img width={32} height={32} src={coinIcon.value} alt="coin icon" />
                    <div class="coin-info">
                      <div class="coin-name">
                        {exchangeStore.getContractSymbolName(data.symbolId)} {t('永续')}
                      </div>
                      <div class="flex info-tags gap-4">
                        <Tag color={data.direction === OrderDirection.Buy ? theme.value.colors.up : theme.value.colors.down}>{contractOrderListStore.getOrderType(data)}</Tag>
                        <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                          {t('冰山委托')}
                        </Tag>
                        <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                          {data.marginType === 1 ? t('逐仓') : t('全仓')}
                          {data.leverage}X
                        </Tag>
                        <Tag color={strategyStatus.value.statusColor} bgColor={theme.value.colors.bg.tag.base}>
                          {strategyStatus.value.status}
                        </Tag>
                      </div>
                    </div>
                  </div>

                  {/* 运行时长 */}
                  <div class="position-info">
                    <ListCell title={t('运行时长')} description={runtime.value} />
                  </div>
                </PositionItemLayout>

                {/* 标签页 */}
                <Tabs tabHeight={42} v-model={activeTab.value}>
                  <TabPane label={t('策略信息')} name={StrategyDetailTab.StrategyInfo}>
                    <div class="tab-content">
                      {/* 基本信息 */}
                      <div class="position-info" style={{ marginTop: '0 !important' }}>
                        <ListCell
                          title={t('委托总量')}
                          description={`${contractOrderListStore.getIcebergTotalQty(data).formatCommonMoney()} ${contractOrderListStore.getOpenName(data.symbolId)}`}
                        />
                        <ListCell
                          title={t('已成交量')}
                          description={`${contractOrderListStore.getIcebergDealInterestQty(data).formatCommonMoney()} ${contractOrderListStore.getOpenName(data.symbolId)}`}
                        />
                        <ListCell title={t('挂单笔数')} description={String(data.orderNum)} />
                      </div>

                      {/* 高级设置 */}
                      <div style={{ marginTop: '24px' }}>
                        <div class="section-title">{t('高级设置')}</div>
                        <div class="position-info">
                          <ListCell title={t('挂单偏好')} description={t(ContractTypeIces[data.preferenceType - 1])} />
                          <ListCell title={t('触发条件')} description={contractOrderListStore.getIcebergStrategyTriggerCondition(data)} />
                        </div>
                      </div>

                      {/* 运行情况 */}
                      <div style={{ marginTop: '24px' }}>
                        <div class="section-title">{t('运行情况')}</div>
                        <div class="position-info">
                          <ListCell title={t('创建时间')} description={dayjs(data.createTime).format('YYYY/MM/DD HH:mm:ss')} />
                          <ListCell title={t('成交均价')} description={avgPrice.value} />
                          {(data.status === 3 || data.status === 4) && <ListCell title={t('完成时间')} description={dayjs(data.updateTime).format('YYYY/MM/DD HH:mm:ss')} />}
                          {data.status === 4 && <ListCell title={t('失败原因')} description={data.reason} />}
                        </div>
                      </div>
                    </div>
                  </TabPane>
                  <TabPane label={t('成交记录')} name={StrategyDetailTab.TradeRecord}>
                    <IcebergTradeHistory strategyId={data.strategyId} />
                  </TabPane>
                </Tabs>
              </div>
            ),
          }}
        </HocElDialog>
      )
    }
  },
})
